import { signupSchema } from "@caloteiros/shared";
import type { SignupInput } from "@caloteiros/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ReactElement } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import logo from "../assets/logowtext.svg";
import { Field, Spinner } from "../components/ui";
import { ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast } from "../lib/toast";

export function SignupPage(): ReactElement {
  const { signup, googleLogin } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignupInput>({ resolver: zodResolver(signupSchema) });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await signup(values);
      toast.success("Conta criada! Bora dividir a conta.");
      navigate("/app", { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.code === "EMAIL_TAKEN") {
        setError("email", { message: error.message });
        return;
      }
      toast.error(error instanceof ApiError ? error.message : "Não consegui criar a conta agora.");
    }
  });

  return (
    <main className="auth-page">
      <Link to="/">
        <img className="auth-logo" src={logo} alt="Caloteiros" />
      </Link>

      <div className="stack">
        <h1>Criar conta</h1>
        <p className="muted">A sua conta basta: os amigos você adiciona só pelo nome.</p>
      </div>

      <form className="stack" onSubmit={onSubmit} noValidate>
        <Field label="Nome" error={errors.name?.message}>
          <input
            className="input"
            autoComplete="name"
            aria-invalid={Boolean(errors.name)}
            {...register("name")}
          />
        </Field>

        <Field label="E-mail" error={errors.email?.message}>
          <input
            className="input"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            aria-invalid={Boolean(errors.email)}
            {...register("email")}
          />
        </Field>

        <Field
          label="Senha"
          error={errors.password?.message}
          hint="Pelo menos 8 caracteres."
        >
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            aria-invalid={Boolean(errors.password)}
            {...register("password")}
          />
        </Field>

        <button type="submit" className="btn btn-primary btn-block" disabled={isSubmitting}>
          {isSubmitting ? <Spinner /> : "Criar conta"}
        </button>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
          <GoogleLogin
            onSuccess={async (credentialResponse) => {
              if (credentialResponse.credential) {
                try {
                  await googleLogin(credentialResponse.credential);
                  navigate("/app", { replace: true });
                } catch (error) {
                  toast.error(error instanceof ApiError ? error.message : "Erro ao cadastrar com Google.");
                }
              }
            }}
            onError={() => {
              toast.error("Erro ao cadastrar com Google.");
            }}
          />
        </div>
      </form>

      <p className="center muted">
        Já tem conta? <Link to="/entrar">Entrar</Link>
      </p>
    </main>
  );
}
